/**
 * Backgammon Board Component
 *
 * 24 points in 4 quadrants, checkers as stacked circles, bar in center,
 * bearing off areas, dice display. Click-to-select and click-to-move.
 */

import React, { useCallback, useMemo } from 'react';
import {
  BackgammonBoardProps,
  BackgammonMove,
  PlayerSide,
  P1_BAR,
  P2_BAR,
  CHECKERS_PER_PLAYER,
} from '../types';
import './BackgammonBoard.css';

const BackgammonBoard: React.FC<BackgammonBoardProps> = ({
  board,
  currentPlayer,
  dice,
  validMoves,
  selectedPoint,
  borneOff,
  onPointClick,
  onBearOff,
  disabled = false,
  gameStatus,
}) => {
  const movesFromSelected = useMemo(() => {
    if (selectedPoint === null) return [];
    return validMoves.filter(m => m.from === selectedPoint);
  }, [selectedPoint, validMoves]);

  const highlightedTargets = useMemo(() => {
    return new Set(movesFromSelected.map(m => m.to));
  }, [movesFromSelected]);

  const selectablePoints = useMemo(() => {
    return new Set(validMoves.map(m => m.from));
  }, [validMoves]);

  const handlePointClick = useCallback(
    (point: number) => {
      if (disabled || gameStatus !== 'moving') return;
      onPointClick(point);
    },
    [disabled, gameStatus, onPointClick]
  );

  const handleBearOffClick = useCallback(() => {
    if (disabled || gameStatus !== 'moving') return;
    onBearOff();
  }, [disabled, gameStatus, onBearOff]);

  // Render checkers stacked on a point
  const renderCheckers = (point: number) => {
    const count = board[point];
    const absCount = Math.abs(count);
    const isP1 = count > 0;
    const color = isP1 ? 'dark' : 'light';
    const maxVisible = 5;
    const displayCount = Math.min(absCount, maxVisible);

    return (
      <div className="backgammon-board__checkers">
        {Array.from({ length: displayCount }, (_, i) => (
          <div
            key={i}
            className={`backgammon-board__checker backgammon-board__checker--${color}`}
            aria-hidden="true"
          />
        ))}
        {absCount > maxVisible && (
          <span className="backgammon-board__checker-count">{absCount}</span>
        )}
      </div>
    );
  };

  // Render a single point (triangle)
  const renderPoint = (pointNum: number, position: 'top' | 'bottom') => {
    const count = board[pointNum];
    const absCount = Math.abs(count);
    const isSelected = selectedPoint === pointNum;
    const isTarget = highlightedTargets.has(pointNum);
    const isSelectable = selectablePoints.has(pointNum) && !disabled;
    const isEven = pointNum % 2 === 0;

    const classes = [
      'backgammon-board__point',
      `backgammon-board__point--${position}`,
      isEven ? 'backgammon-board__point--even' : 'backgammon-board__point--odd',
      isSelected ? 'backgammon-board__point--selected' : '',
      isTarget ? 'backgammon-board__point--target' : '',
      isSelectable ? 'backgammon-board__point--selectable' : '',
    ].filter(Boolean).join(' ');

    return (
      <button
        key={pointNum}
        className={classes}
        onClick={() => handlePointClick(pointNum)}
        disabled={disabled && !isTarget}
        aria-label={`Point ${pointNum}: ${absCount > 0 ? `${absCount} ${count > 0 ? 'dark' : 'light'} checkers` : 'empty'}${isTarget ? ' (valid move target)' : ''}`}
        tabIndex={isSelectable || isTarget ? 0 : -1}
      >
        <div className="backgammon-board__triangle" aria-hidden="true" />
        {absCount > 0 && renderCheckers(pointNum)}
        {isTarget && (
          <div className="backgammon-board__target-indicator" aria-hidden="true" />
        )}
        <span className="backgammon-board__point-label">{pointNum}</span>
      </button>
    );
  };

  // Top row: points 13-24 (left to right for P1 perspective)
  const topPoints = Array.from({ length: 12 }, (_, i) => 13 + i);
  const topLeft = topPoints.slice(0, 6);   // 13-18
  const topRight = topPoints.slice(6, 12); // 19-24

  // Bottom row: points 12-1 (left to right for P1 perspective)
  const bottomPoints = Array.from({ length: 12 }, (_, i) => 12 - i);
  const bottomLeft = bottomPoints.slice(0, 6);   // 12-7
  const bottomRight = bottomPoints.slice(6, 12); // 6-1

  const p1BarCount = board[P1_BAR];
  const p2BarCount = Math.abs(board[P2_BAR]);
  const p1BearOffTarget = highlightedTargets.has(0);
  const p2BearOffTarget = highlightedTargets.has(25);

  return (
    <div className="backgammon-board" role="grid" aria-label="Backgammon game board">
      {/* Dice display */}
      {dice && (
        <div className="backgammon-board__dice" role="status" aria-live="polite">
          <div className="backgammon-board__die">{renderDieFace(dice.die1)}</div>
          <div className="backgammon-board__die">{renderDieFace(dice.die2)}</div>
          {dice.movesRemaining.length > 0 && (
            <span className="backgammon-board__moves-left">
              {dice.movesRemaining.length} move{dice.movesRemaining.length !== 1 ? 's' : ''} left
            </span>
          )}
        </div>
      )}

      <div className="backgammon-board__frame">
        {/* P2 bearing off area (top-right) */}
        <div className="backgammon-board__bearoff backgammon-board__bearoff--p2">
          <button
            className={`backgammon-board__bearoff-area ${p2BearOffTarget ? 'backgammon-board__bearoff-area--target' : ''}`}
            onClick={handleBearOffClick}
            disabled={!p2BearOffTarget}
            aria-label={`Player 2 borne off: ${borneOff.player2} of ${CHECKERS_PER_PLAYER}`}
          >
            <span className="backgammon-board__bearoff-label">OFF</span>
            <div className="backgammon-board__bearoff-checkers">
              {Array.from({ length: Math.min(borneOff.player2, 5) }, (_, i) => (
                <div key={i} className="backgammon-board__checker backgammon-board__checker--light backgammon-board__checker--small" />
              ))}
            </div>
            <span className="backgammon-board__bearoff-count">{borneOff.player2}/{CHECKERS_PER_PLAYER}</span>
          </button>
        </div>

        {/* Top half: points 13-24 */}
        <div className="backgammon-board__half backgammon-board__half--top">
          <div className="backgammon-board__quadrant">
            {topLeft.map(p => renderPoint(p, 'top'))}
          </div>

          {/* Bar (center) */}
          <div className="backgammon-board__bar">
            <button
              className={`backgammon-board__bar-section backgammon-board__bar-section--p2 ${
                selectablePoints.has(P2_BAR) ? 'backgammon-board__bar-section--selectable' : ''
              } ${selectedPoint === P2_BAR ? 'backgammon-board__bar-section--selected' : ''}`}
              onClick={() => handlePointClick(P2_BAR)}
              disabled={!selectablePoints.has(P2_BAR)}
              aria-label={`Player 2 bar: ${p2BarCount} checkers`}
            >
              {p2BarCount > 0 && (
                <>
                  <div className="backgammon-board__checker backgammon-board__checker--light" />
                  {p2BarCount > 1 && (
                    <span className="backgammon-board__bar-count">{p2BarCount}</span>
                  )}
                </>
              )}
            </button>
          </div>

          <div className="backgammon-board__quadrant">
            {topRight.map(p => renderPoint(p, 'top'))}
          </div>
        </div>

        {/* Bottom half: points 12-1 */}
        <div className="backgammon-board__half backgammon-board__half--bottom">
          <div className="backgammon-board__quadrant">
            {bottomLeft.map(p => renderPoint(p, 'bottom'))}
          </div>

          {/* Bar (center) */}
          <div className="backgammon-board__bar">
            <button
              className={`backgammon-board__bar-section backgammon-board__bar-section--p1 ${
                selectablePoints.has(P1_BAR) ? 'backgammon-board__bar-section--selectable' : ''
              } ${selectedPoint === P1_BAR ? 'backgammon-board__bar-section--selected' : ''}`}
              onClick={() => handlePointClick(P1_BAR)}
              disabled={!selectablePoints.has(P1_BAR)}
              aria-label={`Player 1 bar: ${p1BarCount} checkers`}
            >
              {p1BarCount > 0 && (
                <>
                  <div className="backgammon-board__checker backgammon-board__checker--dark" />
                  {p1BarCount > 1 && (
                    <span className="backgammon-board__bar-count">{p1BarCount}</span>
                  )}
                </>
              )}
            </button>
          </div>

          <div className="backgammon-board__quadrant">
            {bottomRight.map(p => renderPoint(p, 'bottom'))}
          </div>
        </div>

        {/* P1 bearing off area (bottom-right) */}
        <div className="backgammon-board__bearoff backgammon-board__bearoff--p1">
          <button
            className={`backgammon-board__bearoff-area ${p1BearOffTarget ? 'backgammon-board__bearoff-area--target' : ''}`}
            onClick={handleBearOffClick}
            disabled={!p1BearOffTarget}
            aria-label={`Player 1 borne off: ${borneOff.player1} of ${CHECKERS_PER_PLAYER}`}
          >
            <span className="backgammon-board__bearoff-label">OFF</span>
            <div className="backgammon-board__bearoff-checkers">
              {Array.from({ length: Math.min(borneOff.player1, 5) }, (_, i) => (
                <div key={i} className="backgammon-board__checker backgammon-board__checker--dark backgammon-board__checker--small" />
              ))}
            </div>
            <span className="backgammon-board__bearoff-count">{borneOff.player1}/{CHECKERS_PER_PLAYER}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

/** Render a die face with dots */
function renderDieFace(value: number): React.ReactNode {
  return (
    <span className="backgammon-board__die-face" aria-label={`Die showing ${value}`}>
      {['⚀', '⚁', '⚂', '⚃', '⚄', '⚅'][value - 1]}
    </span>
  );
}

export { BackgammonBoard };
export default BackgammonBoard;
